import { HttpContext } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of, timer } from 'rxjs';
import { catchError, exhaustMap, map, switchMap, tap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { AuthService, UsersService } from '../../_generated';
import { IS_AUTH_INTERCEPTOR_ENABLED } from '../../auth';
import { AuthActions } from '../actionstypes/auth/auth.actiontype';
import { AuthState } from '../reducers/auth.reducer';

@Injectable()
export class AuthEffect {
  private readonly actions$ = inject(Actions);
  private readonly userService = inject(UsersService);
  private readonly authService = inject(AuthService);
  private readonly store = inject(Store<AuthState>);
  private readonly router = inject(Router);

  logout$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.logout),
      switchMap(() => this.authService.authControllerLogout().pipe(map(() => AuthActions.logoutSuccess()))),
    ),
  );

  logoutSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.logoutSuccess),
        switchMap(() => this.router.navigate(['/login'])),
      ),
    { dispatch: false },
  );

  signIn$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.signIn),
      switchMap(({ email, password }) =>
        this.authService.authControllerSignIn({ email, password }).pipe(
          map((response) => {
            if (response.message === 'NEW_PASSWORD_REQUIRED') {
              return AuthActions.changePasswordRequired({ response });
            } else if (response.message === 'PASSWORD_RESET_REQUIRED') {
              return AuthActions.confirmChangePasswordRequired({ response });
            }
            return AuthActions.authenticateUser();
          }),
          catchError((error: Error) => of(AuthActions.signInFailure({ error: error.message }))),
        ),
      ),
    ),
  );

  challengeChangePassword$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.changePassword),
      switchMap(({ email, newPassword, session }) => {
        return this.authService
          .authControllerChangePasswordChallenged({
            email,
            newPassword,
            session,
          })
          .pipe(
            map(() => AuthActions.authenticateUser()),
            catchError((error) =>
              of(
                AuthActions.changePasswordFailure({
                  error: error.error.message,
                }),
              ),
            ),
          );
      }),
    ),
  );

  confirmChangePassword$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.confirmChangePassword),
      switchMap(({ email, confirmationCode, newPassword }) => {
        return this.authService
          .authControllerConfirmForgotPassword({
            email,
            confirmationCode,
            newPassword,
          })
          .pipe(
            map(() => AuthActions.confirmChangePasswordSuccess()),
            catchError((error) => {
              console.log(error);
              return of(
                AuthActions.confirmChangePasswordFailure({
                  error: error.error.message,
                }),
              );
            }),
          );
      }),
    ),
  );

  confirmChangePasswordSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.confirmChangePasswordSuccess),
      switchMap(() => of(AuthActions.authenticateUser())),
    ),
  );

  confirmChangePasswordFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.changePasswordFailure),
        switchMap(() => this.router.navigate(['/login'])),
      ),
    { dispatch: false },
  );

  authenticateUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.authenticateUser),
      switchMap(() =>
        this.userService
          .usersControllerGetMe(undefined, undefined, {
            context: new HttpContext().set(IS_AUTH_INTERCEPTOR_ENABLED, false),
          })
          .pipe(
            map((user) => AuthActions.authenticateUserSuccess({ user: user })),
            catchError((error: Error) => of(AuthActions.authenticateUserFailure({ error: error.message }))),
          ),
      ),
    ),
  );

  authenticateUserSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.authenticateUserSuccess),
      switchMap(() => [AuthActions.startRefreshTokensTimer()]),
    ),
  );

  startRefreshTokensInterval$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.startRefreshTokensTimer),
        switchMap(() => {
          return timer(environment.refreshTokensTimerDue).pipe(tap(() => this.store.dispatch(AuthActions.refreshTokens())));
        }),
      ),
    { dispatch: false },
  );

  refreshTokens$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.refreshTokens),
      exhaustMap(() =>
        this.authService.authControllerRefreshTokens().pipe(
          map(() => {
            return AuthActions.refreshTokensSuccess();
          }),
          catchError((error: Error) => of(AuthActions.refreshTokensFailure({ error: error.message }))),
        ),
      ),
    ),
  );

  refreshTokensSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.refreshTokensSuccess),
      switchMap(() => [AuthActions.startRefreshTokensTimer()]),
    ),
  );

  // Some cases we want to refresh the tokens again (seems after some error we dont get redirected to login)
  // might want to review this later
  refreshTokensFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.refreshTokensFailure),
      switchMap(() => [AuthActions.startRefreshTokensTimer()]),
    ),
  );

  forgotPassword$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.forgotPassword),
      switchMap(({ email }) =>
        this.authService.authControllerForgotPassword({ email }).pipe(
          map((response) => AuthActions.forgotPasswordSuccess({ message: response.message })),
          catchError((error) => of(AuthActions.forgotPasswordFailure({ error: error.error.message }))),
        ),
      ),
    ),
  );

  confirmForgotPassword$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.confirmForgotPassword),
      switchMap(({ email, confirmationCode, newPassword }) =>
        this.authService
          .authControllerConfirmForgotPassword({
            email,
            confirmationCode,
            newPassword,
          })
          .pipe(
            map(() => AuthActions.confirmForgotPasswordSuccess()),
            catchError((error) => of(AuthActions.confirmForgotPasswordFailure({ error: error.error.message }))),
          ),
      ),
    ),
  );

  confirmForgotPasswordSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.confirmForgotPasswordSuccess),
      switchMap(() => of(AuthActions.authenticateUser())),
    ),
  );
}
